import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiContactInfoCard,
  MiDetailFields,
  MiMap,
  MiMarker,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  INPUT_TYPES,
  MODAL_TITLES,
  PERMISSIONS,
  POWER_TYPE_OPTIONS,
  regionCodesUS,
  urlRegex,
} from "@miplatform/constants";
import { useMapForm } from "@miplatform/hooks";
import { useCityViewModel } from "@miplatform/view-models";
import { zodResolver } from "@hookform/resolvers/zod";
import { Footer } from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { useMap } from "@vis.gl/react-google-maps";
import { CityAutocomplete } from "../../../components/AddHomeForm/CityAutocomplete/CityAutocomplete";
import { ConfigAccordionHeader } from "../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";

const getPrimaries = (contacts) => {
  return contacts.filter((contact) => contact.isPrimary);
};

const schema = z.object({
  name: z.string().min(1, "Community Name is required"),
  cityId: z.string().min(1, "City is required"),
  regionCode: z.string().min(1, "State is required"),
  builderName: z.string().min(1, "Builder is required"),
  municipalityWebsite: z
    .string()
    .regex(urlRegex, "Invalid URL")
    .optional()
    .or(z.literal("")),
  powerType: z.string().optional(),
  latitude: z
    .number()
    .or(z.string().transform(Number))
    .refine((val) => val >= -90 && val <= 90, {
      message: "Latitude must be between -90 and 90 degrees",
    })
    .refine(
      (val) => {
        const str = val.toString();
        const parts = str.split(".");
        return parts.length < 2 || parts[1].length <= 5;
      },
      { message: "Latitude must have at most 5 decimal places" }
    ),
  longitude: z
    .number()
    .or(z.string().transform(Number))
    .refine((val) => val >= -180 && val <= 180, {
      message: "Longitude must be between -180 and 180 degrees",
    })
    .refine(
      (val) => {
        const str = val.toString();
        const parts = str.split(".");
        return parts.length < 2 || parts[1].length <= 5;
      },
      { message: "Longitude must have at most 5 decimal places" }
    ),
});

export function CommunitySummaryTab({
  community = {},
  contacts = [],
  onSave = () => null,
}) {
  const [openUpdateModal, setOpenUpdateModal] = useState(false);

  const map = useMap();
  const { classes } = useStyles();
  const { allCities, cities, addCityToState, getCities, isLoading } =
    useCityViewModel();

  const defaultValues = useMemo(
    () => ({
      ...community,
      builderName: community?.builder?.name,
      powerType: community?.powerType,
      municipalityWebsite: community?.municipalityWebsite,
      longitude: community?.location?.Longitude,
      latitude: community?.location?.Latitude,
      cityId: community?.cityId || "",
    }),
    [community]
  );

  const {
    formState: { isValid, isDirty },
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    watch,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { hasPermission } = usePermissions();

  const canUpdateCommunity = hasPermission(PERMISSIONS.COMMUNITY.UPDATE);

  const lat = getValues("latitude");
  const lng = getValues("longitude");

  const watchCityId = useWatch({
    control,
    name: "cityId",
    defaultValue: defaultValues.cityId,
  });

  const { handleCoordChange } = useMapForm({
    values: getValues(),
    setValue,
    setError,
    clearErrors,
    watch,
    cities,
    addCity: addCityToState,
    getCities,
    isEditing: true,
  });
  const isFormEditable = canUpdateCommunity;

  const communityFields = [
    {
      label: "Community Name",
      name: "name",
      formControl: control,
      readOnly: !isFormEditable,
      width: "50%",
      disabled: !isFormEditable,
    },
    {
      label: "Builder Name",
      name: "builderName",
      formControl: control,
      readOnly: true,
      width: "50%",
      disabled: true,
    },

    {
      label: "Municipality Website",
      name: "municipalityWebsite",
      formControl: control,
      readOnly: !isFormEditable,
      width: "50%",
      disabled: !isFormEditable,
    },
    {
      label: "Power Type",
      name: "powerType",
      formControl: control,
      readOnly: !isFormEditable,
      width: "50%",
      fieldType: INPUT_TYPES.SELECT,
      options: POWER_TYPE_OPTIONS,
    },
  ];
  const locationFields = [
    {
      label: "State",
      required: true,
      name: "regionCode",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: regionCodesUS.map((code) => ({ label: code, value: code })),
      readOnly: !isFormEditable,
      width: "100%",
    },
    {
      label: "City",
      name: "cityId",
      width: "100%",
      formControl: control,
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <CityAutocomplete
          value={watchCityId}
          cities={allCities}
          onChange={(city) => {
            setValue("cityId", city.id, {
              shouldValidate: true,
              shouldDirty: true,
            });
            setValue("city", city.cityName, {
              shouldValidate: true,
              shouldDirty: true,
            });
            setValue("county", city.countyName, {
              shouldValidate: true,
              shouldDirty: true,
            });
            setValue("regionCode", city.regionCode, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
          onCreate={getCities}
          readOnly={!isFormEditable}
        />
      ),
    },
    {
      label: "Latitude",
      name: "latitude",
      formControl: control,
      readOnly: !isFormEditable,
      type: "number",
      required: true,
      disabled: !isFormEditable,
      width: "100%",
    },
    {
      label: "Longitude",
      name: "longitude",
      formControl: control,
      readOnly: !isFormEditable,
      required: true,
      type: "number",
      disabled: !isFormEditable,
      width: "100%",
    },
  ];

  const getFormDataFromCommunity = (comm) => ({
    name: comm?.name ?? "",
    cityId: comm?.cityId ?? "",
    city: comm?.city?.cityName ?? "",
    county: comm?.city?.countyName ?? "",
    regionCode: comm?.regionCode ?? "",
    builderName: comm?.builder?.name ?? "",
    municipalityWebsite: comm?.municipalityWebsite ?? "",
    powerType: comm?.powerType ?? "",
    latitude: comm?.location?.Latitude ?? 0,
    longitude: comm?.location?.Longitude ?? 0,
  });

  useEffect(() => {
    if (community) {
      reset(getFormDataFromCommunity(community));
      if (map && community.location) {
        map.panTo({
          lng: community?.location?.Longitude,
          lat: community?.location?.Latitude,
        });
      }
    }
  }, [community, map, reset]);
  return (
    <>
      <Box className={classes.summaryContainer} overflow="auto">
        <ConfigAccordionHeader title="Basic Community Information">
          <Box width="100%">
            <MiDetailFields
              detailFields={communityFields}
              containerStyle={{ width: "100%" }}
            />
          </Box>
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Location Information">
          <Box className={classes.mapContainer}>
            <Box flex={1}>
              <MiDetailFields detailFields={locationFields} />
            </Box>
            <Box display="flex" flex={2} padding={1}>
              <MiMap
                enableClick={isFormEditable}
                isReadOnly={!isFormEditable}
                onCoordChange={handleCoordChange}
              >
                <MiMarker position={{ lat, lng }} />
              </MiMap>
            </Box>
          </Box>
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Primary Community Contacts">
          <Box display="flex" gap={2} flexWrap="wrap">
            {getPrimaries(contacts).map((contact) => (
              <Box width="50%" key={contact.id}>
                <MiContactInfoCard
                  readOnly
                  contact={contact}
                  mainColor="primary"
                />
              </Box>
            ))}
          </Box>
        </ConfigAccordionHeader>
      </Box>
      <Footer
        show={isFormEditable}
        saveTitle="Update Community"
        handleSaveClick={() => setOpenUpdateModal(true)}
        handleCancelClick={() => {
          reset(getFormDataFromCommunity(community));
        }}
        isDirty={isDirty}
        disableSave={!isValid || !isDirty || isLoading}
      />
      <MiModalConfirm
        title={MODAL_TITLES.SAVE}
        textPositive="Yes, Update"
        open={openUpdateModal}
        disableClick={isLoading}
        handleNegative={() => setOpenUpdateModal(false)}
        handlePositive={() => {
          onSave({
            ...getValues(),
            location: { latitude: lat, longitude: lng },
          });
          setOpenUpdateModal(false);
        }}
        description={`You are about to save changes to the ${community.name}. Are you sure you would like to continue?`}
        buttonPositiveColor={BUTTON_COLORS.PRIMARY}
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  summaryContainer: {
    height: "inherit",
    display: "flex",
    flex: 1,
    gap: theme.spacing(2),
    overflow: "auto",
    padding: theme.spacing(2),
    flexDirection: "column",
  },
  innerLeftContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    flex: "1 1 50%",
  },
  innerRightContainer: {
    flex: "1 1 30%",
  },
  mapContainer: {
    display: "flex",
    gap: theme.spacing(2),
  },
  pictureContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: theme.spacing(5),
    padding: theme.spacing(0, 3),
    flex: "1 1 15%",
    gap: theme.spacing(3),
  },
  editIcon: {
    cursor: "pointer",
  },
}));
